/* eslint-disable enforce-ids-in-jsx/missing-ids */
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import { useOutsideClick } from 'utils/hooks/useClickOutside'

import { Close, Dislike, Like } from 'components/lib/DesignSystem/Icons'
import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'
import TextInput from 'components/lib/DesignSystem/V3/TextInput'

export const FEEDBACK_COMMENT_MAX_LENGTH = 48

const Container = styled.div`
  position: relative;
  display: inline-flex;
`

const ButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const RatingButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: var(--t-spacing-1-5);
  border: 1px solid
    ${({ $active }) =>
      $active ? 'var(--feedback-rating-border-active)' : 'var(--feedback-rating-border)'};
  border-radius: var(--t-border-radius-md);
  background: ${({ $active }) => ($active ? 'var(--feedback-rating-bg-active)' : 'transparent')};
  color: var(--feedback-rating-icon-color);
  cursor: ${({ disabled, $active }) =>
    disabled ? 'not-allowed' : $active ? 'default' : 'pointer'};
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};

  svg,
  svg > path {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }

  &:hover {
    ${({ disabled, $active }) =>
      !disabled && !$active
        ? `
          border-color: var(--feedback-rating-border-active);
          background: var(--feedback-rating-bg-hover);
        `
        : ''}
  }
`

const PopoverContainer = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 290px;
  padding: 16px;
  background: var(--color-elevated-solid);
  border: 1px solid var(--color-elevated-border);
  border-radius: 12px;
  box-shadow: var(--color-elevated-shadow);
`

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const PopoverTitle = styled.div`
  color: var(--color-text);
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-sm);
`

const CloseIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--color-text);

  svg,
  svg > path {
    fill: currentColor;
  }
`

const PopoverSubmitRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

const FeedbackCommentPopover = ({ idPrefix, itemId, maxLength, onClose, onSubmit }) => {
  const [comment, setComment] = useState('')
  const trimmedComment = comment.trim()
  const isSubmitDisabled = trimmedComment.length === 0

  const handleSubmit = () => {
    if (isSubmitDisabled) return
    onSubmit(trimmedComment)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  return (
    <PopoverContainer
      id={`${idPrefix}_FeedbackComment_Popover_div-${itemId}`}
      data-testid={`${idPrefix}_FeedbackComment_Popover_div-${itemId}`}
      data-feedback-popover=""
      onClick={(e) => e.stopPropagation()}
    >
      <PopoverHeader>
        <PopoverTitle>Tell us more</PopoverTitle>
        <CloseIconButton
          id={`${idPrefix}_FeedbackComment_Close_button-${itemId}`}
          data-testid={`${idPrefix}_FeedbackComment_Close_button-${itemId}`}
          aria-label="Close feedback popover"
          type="button"
          onClick={onClose}
        >
          <Close width="20px" height="20px" />
        </CloseIconButton>
      </PopoverHeader>
      <TextInput
        id={`${idPrefix}_FeedbackComment_Input-${itemId}`}
        dataTestId={`${idPrefix}_FeedbackComment_Input-${itemId}`}
        size="xs"
        fullWidth
        autoFocus
        maxLength={maxLength}
        value={comment}
        placeholder="Why is this not a good suggestion?"
        onChange={(event) => setComment(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <PopoverSubmitRow>
        <Button
          id={`${idPrefix}_FeedbackComment_Submit_button-${itemId}`}
          dataTestId={`${idPrefix}_FeedbackComment_Submit_button-${itemId}`}
          size={SIZES.XS}
          variant={VARIANTS.TERTIARY}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </PopoverSubmitRow>
    </PopoverContainer>
  )
}

/**
 * Shared like / dislike control (PSIMA visuals).
 *
 * Presentational + popover state only — persistence and dispatch stay with the
 * consumer. Negative feedback requires a comment (max {commentMaxLength} chars):
 * the dislike is only reported via `onDislike` on submit, so closing the popover
 * never reports an empty dislike.
 */
const FeedbackRatingControl = ({
  itemId,
  idPrefix = 'Feedback',
  selected = null,
  onLike,
  onDislike,
  commentMaxLength = FEEDBACK_COMMENT_MAX_LENGTH,
  pendoFeatureUp,
  pendoFeatureDown,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const hasSelection = selected !== null

  const handleClosePopover = useCallback(() => setIsPopoverOpen(false), [])
  const containerRef = useOutsideClick(handleClosePopover)

  const handleLikeClick = () => {
    if (hasSelection) return
    onLike?.()
  }

  const handleDislikeClick = () => {
    if (hasSelection) return
    setIsPopoverOpen(true)
  }

  const handleSubmitComment = (comment) => {
    onDislike?.(comment)
    setIsPopoverOpen(false)
  }

  const isLikeSelected = selected === 'like'
  const isDislikeSelected = selected === 'dislike'

  return (
    <Container
      ref={containerRef}
      id={`${idPrefix}_FeedbackRating_div-${itemId}`}
      data-testid={`${idPrefix}_FeedbackRating_div-${itemId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <ButtonsGroup>
        <RatingButton
          id={`${idPrefix}_Like_button-${itemId}`}
          data-test-id={`${idPrefix}_Like_button-${itemId}`}
          data-pendo-feature={pendoFeatureUp}
          aria-label="Good suggestion"
          type="button"
          $active={isLikeSelected}
          disabled={hasSelection && !isLikeSelected}
          onClick={handleLikeClick}
        >
          <Like />
        </RatingButton>
        <RatingButton
          id={`${idPrefix}_Dislike_button-${itemId}`}
          data-test-id={`${idPrefix}_Dislike_button-${itemId}`}
          data-pendo-feature={pendoFeatureDown}
          aria-label="Not a good suggestion"
          type="button"
          $active={isDislikeSelected || isPopoverOpen}
          disabled={hasSelection && !isDislikeSelected}
          onClick={handleDislikeClick}
        >
          <Dislike />
        </RatingButton>
      </ButtonsGroup>
      {isPopoverOpen && (
        <FeedbackCommentPopover
          idPrefix={idPrefix}
          itemId={itemId}
          maxLength={commentMaxLength}
          onClose={handleClosePopover}
          onSubmit={handleSubmitComment}
        />
      )}
    </Container>
  )
}

export default FeedbackRatingControl
